import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, CheckCircle, XCircle, AlertTriangle, FileText, Clock, X, RefreshCw, Calendar, User, CreditCard, Building } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { getLocalApiUrl, authFetch } from '../../config/api';
// Removed PageHeader import

const PatronTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [touchPoints, setTouchPoints] = useState(['ALL']);
  const [isLoadingTouchPoints, setIsLoadingTouchPoints] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50,
    totalCount: 0,
    hasMorePages: false
  });
  
  const [searchFilters, setSearchFilters] = useState({
    touchPoint: 'ALL',
    documentType: '',
    failureReason: '',
    transactionType: '',
    searchText: '',
    accountID: '',
    masterProfileID: ''
  });

  // Helper function to format local date as YYYY-MM-DD
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [dateRange, setDateRange] = useState({
    fromDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 1);
      return formatLocalDate(date);
    })(),
    toDate: formatLocalDate(new Date())
  });

  // Add state for profile details modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  
  // Add state for client transactions
  const [clientTransactions, setClientTransactions] = useState([]);
  const [clientTransactionsLoading, setClientTransactionsLoading] = useState(false);
  const [clientTransactionsError, setClientTransactionsError] = useState(null);
  const [showClientTransactions, setShowClientTransactions] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  // Load touchpoints on component mount
  useEffect(() => {
    fetchTouchPoints();
  }, []);

  // Utility function to format date to yyyy-MM-ddTHH:mm:ss
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Get date range from state
  const getDateRange = () => {
    const fromDate = new Date(dateRange.fromDate);
    fromDate.setHours(0, 0, 0, 0);
    
    const toDate = new Date(dateRange.toDate);
    toDate.setHours(23, 59, 59, 999);
    
    return {
      logFromTime: formatDateForAPI(fromDate),
      logToTime: formatDateForAPI(toDate)
    };
  };

  // Touchpoints: distinct values from patron_transactions (no fabricated list)
  const fetchTouchPoints = async () => {
    setIsLoadingTouchPoints(true);
    try {
      const response = await authFetch(getLocalApiUrl('/api/patron-transactions/touch-points'));
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw new Error(json.error || `Request failed (${response.status})`);
      }
      const list = Array.isArray(json.data) ? json.data : ['ALL'];
      setTouchPoints(list.length ? list : ['ALL']);
    } catch (error) {
      console.error('Error fetching touchpoints:', error);
      setTouchPoints(['ALL']);
    } finally {
      setIsLoadingTouchPoints(false);
    }
  };

  // API call to fetch success transactions
  const fetchSuccessTransactions = async () => {
    setIsLoading(true);
    try {
      const dateRangeValues = getDateRange();
      const params = new URLSearchParams({
        page: pagination.currentPage.toString(),
        pageSize: pagination.pageSize.toString()
      });

      if (searchFilters.touchPoint && searchFilters.touchPoint !== 'ALL') {
        params.set('touchPoint', searchFilters.touchPoint);
      }
      if (searchFilters.documentType) params.set('documentType', searchFilters.documentType);
      if (searchFilters.failureReason) params.set('failureReason', searchFilters.failureReason);
      if (searchFilters.transactionType) params.set('transactionType', searchFilters.transactionType);
      if (searchFilters.masterProfileID) params.set('masterProfileID', searchFilters.masterProfileID);
      if (searchFilters.accountID) params.set('accountID', searchFilters.accountID);
      if (searchFilters.searchText) params.set('searchText', searchFilters.searchText);
      if (dateRangeValues.logFromTime) params.set('fromDate', dateRangeValues.logFromTime);
      if (dateRangeValues.logToTime) params.set('toDate', dateRangeValues.logToTime);

      const response = await authFetch(getLocalApiUrl(`/api/patron-transactions?${params.toString()}`));

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setTransactions(result.data);
        setFilteredTransactions(filterTransactions(result.data));
        setPagination(prev => ({
          ...prev,
          totalCount: result.totalCount,
          hasMorePages: result.hasMorePages
        }));
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error fetching success transactions:', error);
      setTransactions([]);
      setFilteredTransactions([]);
      setPagination(prev => ({ ...prev, totalCount: 0, hasMorePages: false }));
    } finally {
      setIsLoading(false);
    }
  };

  // Filter transactions based on search text
  const filterTransactions = (transactions) => {
    if (!searchFilters.searchText.trim()) {
      return transactions;
    }
    
    const searchTerm = searchFilters.searchText.toLowerCase();
    return transactions.filter(transaction => 
      transaction.touchPoint.toLowerCase().includes(searchTerm) ||
      transaction.operation.toLowerCase().includes(searchTerm) ||
      transaction.verificationPurpose.toLowerCase().includes(searchTerm) ||
      transaction.identificationType.toLowerCase().includes(searchTerm) ||
      transaction.method.toLowerCase().includes(searchTerm) ||
      transaction.documentType.toLowerCase().includes(searchTerm) ||
      transaction.status.toLowerCase().includes(searchTerm) ||
      transaction.failureReason.toLowerCase().includes(searchTerm) ||
      (transaction.masterProfileID && transaction.masterProfileID.toLowerCase().includes(searchTerm)) ||
      (transaction.accountID && transaction.accountID.toLowerCase().includes(searchTerm)) ||
      (transaction.memberID && transaction.memberID.toLowerCase().includes(searchTerm))
    );
  };

  // Pagination functions
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  // Search function
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchSuccessTransactions();
  };

  // Update filtered transactions when search text changes
  useEffect(() => {
    const filtered = filterTransactions(transactions);
    setFilteredTransactions(filtered);
    
    // Reset to first page when search changes
    if (searchFilters.searchText.trim()) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
  }, [searchFilters.searchText, transactions]);

  // Fetch data when pagination changes
  useEffect(() => {
    fetchSuccessTransactions();
  }, [pagination.currentPage, pagination.pageSize]);

  // Load data on component mount
  useEffect(() => {
    fetchSuccessTransactions();
  }, []);

  const getStatusBadge = (status) => {
    if (status === 'Success') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Success
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </span>
      );
    }
  };

  // Add function to fetch profile details
  const fetchProfileDetails = async (transaction) => {
    if (!transaction.masterProfileID) {
      setProfileError('No Master Profile ID available');
      return;
    }

    setProfileLoading(true);
    setProfileError(null);
    setSelectedProfile(null);

    try {
      const response = await authFetch(
        getLocalApiUrl(`/api/patron-transactions/profile/${encodeURIComponent(transaction.masterProfileID)}`)
      );

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setSelectedProfile(result.data);
      } else {
        setProfileError(result.message || 'No profile data found for this Master Profile ID');
      }
    } catch (error) {
      console.error('Error fetching profile details:', error);
      setProfileError('Failed to fetch profile details. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  // API call to fetch client transactions
  const fetchClientTransactions = async (transactionId) => {
    setClientTransactionsLoading(true);
    setClientTransactionsError(null);

    try {
      const response = await authFetch(
        getLocalApiUrl(`/api/patron-transactions/client-transactions/${encodeURIComponent(transactionId)}`)
      );

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setClientTransactions(result.data || []);
        setShowClientTransactions(true);
      } else {
        setClientTransactions([]);
        setShowClientTransactions(true);
      }
    } catch (error) {
      console.error('Error fetching client transactions:', error);
      setClientTransactionsError('Failed to fetch client transactions');
      setClientTransactions([]);
      setShowClientTransactions(true);
    } finally {
      setClientTransactionsLoading(false);
    }
  };

  // Update the showDetails function
  const showDetails = (transaction) => {
    if (transaction.masterProfileID && transaction.masterProfileID.trim()) {
      fetchProfileDetails(transaction);
      setSelectedTransactionId(transaction.id);
      setShowProfileModal(true);
    }
  };

  // Add function to close profile modal
  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedProfile(null);
    setProfileError(null);
    setClientTransactions([]);
    setClientTransactionsError(null);
    setShowClientTransactions(false);
    setSelectedTransactionId(null);
  };

  // Add function to handle subtransactions
  const handleShowSubtransactions = (profile) => {
    if (profile && selectedTransactionId) {
      fetchClientTransactions(selectedTransactionId);
    }
  };

  // Add formatTimestamp function
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return timestamp;
    }
  };

  // Format date only (without time) for DOB and similar fields
  const formatDateOnly = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch {
      return timestamp;
    }
  };

  // Add formatTimestampWithMilliseconds function for client transactions
  const formatTimestampWithMilliseconds = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
      
      return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    } catch {
      return timestamp;
    }
  };

  // Handle Enter key press for search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const exportToCSV = () => {
    if (filteredTransactions.length === 0) {
      return;
    }

    // Define CSV headers
    const headers = [
      'Transaction ID',
      'Timestamp',
      'Touch Point',
      'Transaction Type',
      'Verification',
      'Identification Type',
      'Account ID',
      'Member ID',
      'Confidence Score',
      'Status',
      'Failure Reason'
    ];

    // Convert transactions to CSV format
    const csvContent = [
      headers.join(','),
      ...filteredTransactions.map(transaction => [
        `"${transaction.id}"`,
        `"${transaction.timestamp}"`,
        `"${transaction.touchPoint}"`,
        `"${transaction.operation}"`,
        `"${transaction.verificationPurpose}"`,
        `"${transaction.identificationType}"`,
        `"${transaction.accountID || 'N/A'}"`,
        `"${transaction.memberID || 'N/A'}"`,
        `"${transaction.confidence}"`,
        `"${transaction.status}"`,
        `"${transaction.failureReason}"`
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `patron-transactions-${formatLocalDate(new Date())}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add page content directly (no duplicate header)
  return (
    <div className="p-6">
      {/* Search & Filter Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Search & Filter Profile Transactions</h3>
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
        
        {/* First Row - Date Range and Core Search */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div>
            <Label htmlFor="fromDate">From Date</Label>
            <Input
              id="fromDate"
              type="date"
              value={dateRange.fromDate}
              onChange={(e) => setDateRange({...dateRange, fromDate: e.target.value})}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="toDate">To Date</Label>
            <Input
              id="toDate"
              type="date"
              value={dateRange.toDate}
              onChange={(e) => setDateRange({...dateRange, toDate: e.target.value})}
              className="mt-1"
            />
      </div>
          <div>
            <Label htmlFor="accountID">Account ID</Label>
            <Input
              id="accountID"
              type="text"
              placeholder="e.g., 103536"
              value={searchFilters.accountID}
              onChange={(e) => setSearchFilters({...searchFilters, accountID: e.target.value})}
              onKeyPress={handleKeyPress}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="masterProfileID">Master Profile ID</Label>
            <Input
              id="masterProfileID"
              type="text"
              placeholder="e.g., MP12345"
              value={searchFilters.masterProfileID}
              onChange={(e) => setSearchFilters({...searchFilters, masterProfileID: e.target.value})}
              onKeyPress={handleKeyPress}
              className="mt-1"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={handleSearch} disabled={isLoading} className="w-full">
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
          </div>

        {/* Second Row - Additional Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4 relative">
          <div className="relative">
            <Label htmlFor="touchPoint">Touch Point</Label>
            <select
              id="touchPoint"
              value={searchFilters.touchPoint}
              onChange={(e) => setSearchFilters({...searchFilters, touchPoint: e.target.value})}
              disabled={isLoadingTouchPoints}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            >
              {touchPoints.map((touchPoint) => (
                <option key={touchPoint} value={touchPoint}>
                  {touchPoint}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="documentType">Document Type</Label>
            <Input
              id="documentType"
              type="text"
              placeholder="e.g., Driver License, Passport"
              value={searchFilters.documentType}
              onChange={(e) => setSearchFilters({...searchFilters, documentType: e.target.value})}
              onKeyPress={handleKeyPress}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="failureReason">Failure Reason</Label>
            <Input
              id="failureReason"
              type="text"
              placeholder="e.g., OCR Failure, Face Detection"
              value={searchFilters.failureReason}
              onChange={(e) => setSearchFilters({...searchFilters, failureReason: e.target.value})}
              onKeyPress={handleKeyPress}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="transactionType">Transaction Type</Label>
            <Input
              id="transactionType"
              type="text"
              placeholder="e.g., OnBoard, Verify"
              value={searchFilters.transactionType}
              onChange={(e) => setSearchFilters({...searchFilters, transactionType: e.target.value})}
              onKeyPress={handleKeyPress}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="searchText">Search Text</Label>
            <Input
              id="searchText"
              type="text"
              placeholder="Search in transaction details..."
              value={searchFilters.searchText}
              onChange={(e) => setSearchFilters({...searchFilters, searchText: e.target.value})}
              onKeyPress={handleKeyPress}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      <div className="flex h-full">
        {/* Main content */}
        <div className="flex-1">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Touch Point</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Type</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verification</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Identification Type</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confidence Score</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile ID</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Failure Reason</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransactions.length > 0 ? (
                      filteredTransactions.map((transaction) => (
                        <tr 
                          key={transaction.id} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => showDetails(transaction)}
                        >
                          <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{transaction.id}</td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatTimestamp(transaction.timestamp)}</td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.touchPoint}</td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                              {transaction.operation}
                            </span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.verificationPurpose}</td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                              {transaction.identificationType}
                            </span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded ${transaction.confidence !== 'N/A' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {transaction.confidence}
                            </span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getStatusBadge(transaction.status)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-xs">{transaction.masterProfileID || 'N/A'}</td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.failureReason}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="px-3 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <FileText className="h-12 w-12 text-gray-400 mb-4" />
                            <p className="text-lg font-medium">No transactions found</p>
                            <p className="text-sm">Try adjusting your search criteria or date range</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {pagination.totalCount > 0 && (
                <div className="px-3 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-gray-700">
                        Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
                        {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of{' '}
                        {pagination.totalCount} results
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700">Page Size:</label>
                        <select
                          value={pagination.pageSize}
                          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                          className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={200}>200</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage <= 1}
                      >
                        Previous
                      </Button>
                      
                      <span className="text-sm text-gray-700">
                        Page {pagination.currentPage} of {Math.ceil(pagination.totalCount / pagination.pageSize)}
                      </span>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasMorePages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Profile Details Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeProfileModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <button 
                onClick={closeProfileModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              {profileLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading profile details...</p>
            </div>
          </div>
              )}
              
              {profileError && (
                <div className={`rounded-lg p-4 mb-4 ${
                  profileError.includes('No profile data found') 
                    ? 'bg-blue-50 border border-blue-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
            <div className="flex items-center">
                    {profileError.includes('No profile data found') ? (
                      <AlertTriangle className="h-5 w-5 text-blue-500 mr-2" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500 mr-2" />
                    )}
                    <p className={
                      profileError.includes('No profile data found') 
                        ? 'text-blue-800' 
                        : 'text-red-800'
                    }>
                      {profileError}
                </p>
              </div>
            </div>
              )}

              {selectedProfile && (
                <div className="space-y-6">
                  {/* Profile Header */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
                      <div className="flex-shrink-0 mr-4">
                        {selectedProfile.VerifyFPPhotoB64 ? (
                          <img 
                            src={`data:image/jpeg;base64,${selectedProfile.VerifyFPPhotoB64}`} 
                            alt={`${selectedProfile.FN} ${selectedProfile.LN}`}
                            className="h-16 w-16 rounded-full object-cover border-2 border-blue-200"
                          />
                        ) : (
                          <div className="bg-blue-100 rounded-full p-3">
                            <User className="h-8 w-8 text-blue-600" />
                          </div>
                        )}
                      </div>
              <div>
                        <h4 className="text-lg font-bold text-gray-900">
                          {selectedProfile.FN} {selectedProfile.LN}
                        </h4>
                        <p className="text-blue-600 font-mono text-xs">
                          Master Profile ID: {selectedProfile.MasterProfileID}
                </p>
              </div>
            </div>
          </div>

                  {/* Profile Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Personal Information */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-4">
                        <CreditCard className="h-4 w-4 text-gray-600 mr-2" />
                        <h5 className="text-sm font-semibold text-gray-900">Personal Information</h5>
              </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-xs">First Name:</span>
                          <span className="font-medium text-sm">{selectedProfile.FN}</span>
            </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-xs">Last Name:</span>
                          <span className="font-medium text-sm">{selectedProfile.LN}</span>
          </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-xs">Middle Name:</span>
                          <span className="font-medium text-sm">{selectedProfile.MN || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-xs">Date of Birth:</span>
                          <span className="font-medium text-sm">{selectedProfile.DOB ? formatDateOnly(selectedProfile.DOB) : 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Membership Information */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-4">
                        <Building className="h-4 w-4 text-gray-600 mr-2" />
                        <h5 className="text-sm font-semibold text-gray-900">Membership Information</h5>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-xs">Account ID:</span>
                          <span className="font-medium text-sm">{selectedProfile.AID || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-xs">Membership ID:</span>
                          <span className="font-medium text-sm">{selectedProfile.MID || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-xs">Confidence Level:</span>
                          <Badge className={`text-xs ${selectedProfile.IDFPConfident >= 0.8 ? 'bg-green-100 text-green-800' : selectedProfile.IDFPConfident >= 0.6 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                            {selectedProfile.IDFPConfident >= 0.8 ? 'HIGH' : selectedProfile.IDFPConfident >= 0.6 ? 'MEDIUM' : 'LOW'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-xs">Profile Score:</span>
                          <span className="font-medium text-sm">{selectedProfile.IDFPConfident ? `${Math.round(selectedProfile.IDFPConfident * 100)}%` : 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                     
                    {/* System Information */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 md:col-span-2">
                      <div className="flex items-center mb-4">
                        <Calendar className="h-4 w-4 text-gray-600 mr-2" />
                        <h5 className="text-sm font-semibold text-gray-900">System Information</h5>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 text-xs">Created Date:</span>
                          <span className="font-medium text-sm">{selectedProfile.IDFPCreadtedDT ? formatTimestamp(selectedProfile.IDFPCreadtedDT) : 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 text-xs">Created Touch Point:</span>
                          <span className="font-medium text-sm">Semi-Attended</span>
                    </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 text-xs">DVS Status:</span>
                          <Badge variant="default" className="text-xs">
                            VERIFIED
                          </Badge>
                    </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Show Client Transaction Button */}
                  <div className="flex justify-center pt-6 border-t border-gray-200">
                    <Button 
                      variant="outline" 
                      size="lg"
                      onClick={() => handleShowSubtransactions(selectedProfile)}
                      disabled={clientTransactionsLoading}
                      className="w-full max-w-xs"
                    >
                      {clientTransactionsLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 mr-2" />
                          Show Client Transaction
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Client Transactions Section */}
                  {showClientTransactions && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h5 className="text-lg font-semibold text-gray-900 mb-4">Client Transactions</h5>
                      
                      {clientTransactionsError && (
                        <div className="rounded-lg p-4 mb-4 bg-red-50 border border-red-200">
                          <div className="flex items-center">
                            <XCircle className="h-5 w-5 text-red-500 mr-2" />
                            <p className="text-red-800">{clientTransactionsError}</p>
                          </div>
                        </div>
                      )}
                      
                      {clientTransactions.length > 0 ? (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Timestamp
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Client Transaction ID
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Client Transaction Type
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Details
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Confidence Score
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {clientTransactions.map((clientTransaction, index) => (
                                  <tr key={`${clientTransaction.ClientTransactionID}-${index}`} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {formatTimestampWithMilliseconds(clientTransaction.Timestamp)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {clientTransaction.ClientTransactionID || 'N/A'}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {clientTransaction.ClientTransactionType || 'N/A'}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        clientTransaction.Status?.toLowerCase() === 'success' 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {clientTransaction.Status || 'N/A'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500">
                                      {clientTransaction.Details || 'N/A'}
                  </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {clientTransaction.ConfidenceScore || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
                      ) : (
                        <div className="text-center py-8">
                          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">No client transactions found</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PatronTransactions;